import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from "@mui/material";
import { useDataProvider, useNotify, useRefresh } from "react-admin";
import IconButton from "@mui/material/IconButton";
// 在顶部添加新的导入
import JSZip from "jszip";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import CloseIcon from "@mui/icons-material/Close";
import { getTruckPlateColorLabel, TruckVerifyInfo } from "../../models/Truck";
import { UserInfo } from "../../models/response/UserInfo";

interface VerifyTruckDialogProps {
  open: boolean;
  onClose: () => void;
  verifyId: string;
  verifyData: TruckVerifyInfo | null;
}
// 添加图片预览状态接口
interface ImagePreviewState {
  src: string | null;
  rotation: number;
  scale: number;
}

export const VerifyTruckDialog: React.FC<VerifyTruckDialogProps> = ({
  open,
  onClose,
  verifyId,
  verifyData,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [drivingLicenseImage, setDrivingLicenseImage] = useState<string>();
  const [certificate, setCertificate] = useState<string>();
  const [truckLicense, setTruckLicense] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [verifyStatus, setVerifyStatus] = useState<string>("1"); // 1: 通过, 2: 拒绝
  const [rejectReason, setRejectReason] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>();
  // 修改图片预览状态
  const [preview, setPreview] = useState<ImagePreviewState>({
    src: null,
    rotation: 0,
    scale: 1,
  });
  // 添加图片控制函数
  const handleRotateLeft = () => {
    setPreview((prev) => ({
      ...prev,
      rotation: (prev.rotation - 90) % 360,
    }));
  };

  const handleRotateRight = () => {
    setPreview((prev) => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360,
    }));
  };

  const handleZoomIn = () => {
    setPreview((prev) => ({
      ...prev,
      scale: Math.min(prev.scale + 0.5, 3),
    }));
  };

  const handleZoomOut = () => {
    setPreview((prev) => ({
      ...prev,
      scale: Math.max(prev.scale - 0.5, 0.5),
    }));
  };

  const handleClosePreview = () => {
    setPreview({ src: null, rotation: 0, scale: 1 });
  };

  const plateColor = () =>
    getTruckPlateColorLabel(verifyData?.truck?.plate_color);

  useEffect(() => {
    const fetchImages = async () => {
      if (!verifyId) return;

      try {
        setLoading(true);
        const imagesData = await dataProvider.getTruckVerifyImages(verifyId);
        const drivingLicenseImage = await dataProvider.getVerifyImageData(
          imagesData.driving_license,
        );
        const truckLicense = await dataProvider.getVerifyImageData(
          imagesData.truck_license,
        );
        setTruckLicense(truckLicense);
        const userInfosData = await dataProvider.fetchUserInfo(
          verifyData?.truck?.owner ?? "",
        );
        if (userInfosData.data.length > 0) {
          setUserInfo(userInfosData.data[0]);
          const imagesID = await dataProvider.getVerifyImages(
            userInfosData.data[0]?.id,
          );
          // drivingLicenseImage, certificate
          // const drivingLicenseImage = await dataProvider.getVerifyImageData(imagesID.driver_license);
          const certificate = await dataProvider.getVerifyImageData(
            imagesID.certificate,
          );
          setDrivingLicenseImage(drivingLicenseImage);
          setCertificate(certificate);
        }
      } catch (error) {
        console.error("获取认证图片失败:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open && verifyId) {
      fetchImages();
    }
  }, [open, verifyId, dataProvider]);

  const getImageTitle = (type: string) => {
    switch (type) {
      case "truck_license":
        return "道路运输许可证";
      case "driving_license":
        return "车辆行驶证";
      case "qualification":
        return "从业资格证";
      default:
        return "其他证件照";
    }
  };

  const handleSubmit = async () => {
    if (verifyStatus === "-1" && !rejectReason) {
      notify("请填写拒绝原因", { type: "error" });
      return;
    }

    try {
      setSubmitting(true);
      await dataProvider.modifyTruckStatus(verifyId, {
        status: parseInt(verifyStatus),
        reason: verifyStatus === "-1" ? rejectReason : undefined,
      });
      notify("更新成功", { type: "success" });
      refresh();
      onClose();
    } catch (error) {
      notify("更新失败", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // 获取图片名称的辅助函数
  const getImageName = (index: number): string => {
    switch (index) {
      case 0:
        return "道路运输许可证";
      case 1:
        return "车辆行驶证";
      case 2:
        return "从业资格证";
      default:
        return "未知图片";
    }
  };
  // 修改图片容器组件
  const ImageContainer = ({
    src,
    alt,
    title,
  }: {
    src: string;
    alt: string;
    title: string;
  }) => (
    <Box position="relative">
      <img
        src={src}
        alt={alt}
        style={{ width: "100%", maxHeight: "200px", objectFit: "contain" }}
      />
      <IconButton
        onClick={() => setPreview({ src, rotation: 0, scale: 1 })}
        sx={{
          position: "absolute",
          bottom: 8,
          right: 8,
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.9)",
          },
        }}
        size="small"
      >
        <ZoomInIcon />
      </IconButton>
    </Box>
  );

  // 添加下载处理函数
  const handleDownloadImages = async () => {
    const allImages = [truckLicense, drivingLicenseImage].filter(Boolean);
    if (allImages.length === 0) return;

    try {
      const zip = new JSZip();
      const zipFileName = `${userInfo?.real_name ?? "未知用户"}_${verifyData?.plate ?? "未知车牌"}_${verifyData?.vin ?? "未知VIN"}.zip`;

      // 添加图片到zip
      const imagePromises = allImages.map(async (imageUrl, index) => {
        const response = await fetch(imageUrl ?? "");
        const blob = await response.blob();
        const imageName = `${index + 1}_${getImageName(index)}.jpg`;
        zip.file(imageName, blob);
      });

      await Promise.all(imagePromises);

      // 生成并下载zip文件
      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("下载图片失败:", error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>认证详情</Typography>
            <IconButton
              onClick={handleDownloadImages}
              disabled={loading}
              title="下载所有图片"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>
                  基本信息
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography>车主姓名：{userInfo?.real_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>车牌号：{verifyData?.plate}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>VIN码：{verifyData?.vin}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>车牌颜色：{plateColor()}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>
                  证件照片
                </Typography>
                <Grid container spacing={2}>
                  {truckLicense && (
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        {getImageTitle("truck_license")}
                      </Typography>
                      <ImageContainer
                        src={truckLicense}
                        alt="道路运输许可证"
                        title={getImageTitle("truck_license")}
                      />
                    </Grid>
                  )}
                  {drivingLicenseImage && (
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        {getImageTitle("driving_license")}
                      </Typography>
                      <ImageContainer
                        src={drivingLicenseImage}
                        alt="车辆行驶证"
                        title={getImageTitle("driving_license")}
                      />
                    </Grid>
                  )}
                  {certificate && (
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        {getImageTitle("qualification")}
                      </Typography>
                      <ImageContainer
                        src={certificate}
                        alt="从业资格证"
                        title={getImageTitle("qualification")}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </>
          )}
          <Box mt={3}>
            <Typography variant="subtitle1" gutterBottom>
              审核结果
            </Typography>
            <RadioGroup
              value={verifyStatus}
              onChange={(e) => {
                setVerifyStatus(e.target.value);
                if (e.target.value === "1") {
                  setRejectReason("");
                }
              }}
            >
              <FormControlLabel value="1" control={<Radio />} label="通过" />
              <FormControlLabel value="-1" control={<Radio />} label="拒绝" />
            </RadioGroup>

            {verifyStatus === "-1" && (
              <TextField
                fullWidth
                label="拒绝原因"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                required
                error={verifyStatus === "-1" && !rejectReason}
                helperText={
                  verifyStatus === "-1" && !rejectReason ? "请填写拒绝原因" : ""
                }
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>取消</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={submitting || (verifyStatus === "-1" && !rejectReason)}
          >
            {submitting ? <CircularProgress size={24} /> : "提交"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 添加图片预览 Dialog */}
      <Dialog
        open={!!preview.src}
        onClose={handleClosePreview}
        maxWidth={false}
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "black" }}>
          {preview.src && (
            <>
              <img
                src={preview.src}
                alt="预览图"
                style={{
                  width: "100%",
                  height: "90vh",
                  objectFit: "contain",
                  transform: `rotate(${preview.rotation}deg) scale(${preview.scale})`,
                  transition: "transform 0.3s ease",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  display: "flex",
                  gap: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  padding: 1,
                  borderRadius: 1,
                }}
              >
                <IconButton
                  onClick={handleZoomIn}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <ZoomInIcon />
                </IconButton>
                <IconButton
                  onClick={handleZoomOut}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <ZoomOutIcon />
                </IconButton>
                <IconButton
                  onClick={handleRotateLeft}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <RotateLeftIcon />
                </IconButton>
                <IconButton
                  onClick={handleRotateRight}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <RotateRightIcon />
                </IconButton>
                <IconButton
                  onClick={handleClosePreview}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerifyTruckDialog;
